import { storeToRefs } from "pinia";

import {
  organisationFeatures,
  outreachFeatures,
  socialFeatures,
} from "shared/boot/authorization";
import { showLoadErrorPage } from "shared/boot/errors";
import { getLocaleText } from "shared/boot/i18n";
import useProductMenu from "shared/composables/useProductMenu";
import useTeamRoute from "shared/composables/useTeamRoute";
import useTeamRouterViewKey from "shared/composables/useTeamRouterViewKey";
import { streamTypes } from "shared/constants";
import applyThemeConfig from "shared/helpers/applyThemeConfig";
import { normalizeReturnTo } from "shared/helpers/oauth";
import { typecastRouteProps } from "shared/helpers/routing";
import transformKeys from "shared/helpers/transformKeys";
import { InsightsLandingPageType } from "shared/modules/Insights/types";
import { useInsightsDashboardRoutes } from "shared/modules/InsightsDashboard/composables/dashboard/useInsightsDashboardRoutes";
import features from "shared/services/features";
import { useStreamGroupsStore } from "shared/stores/streamGroups";
import { useStreamsStore } from "shared/stores/streams";
import { useUserStore } from "shared/stores/user";
import { AppSection } from "shared/types";

import { PRODUCT_TABS } from "src/components/OrganisationTabs";

const { buildInsightsDashboardRoutes } = useInsightsDashboardRoutes();

const QUERY_SNIPPETS_VALID_PRODUCTS = Object.entries(PRODUCT_TABS)
  .filter(([, tabs]) => tabs.includes("query-snippets"))
  .map(([product]) => product);

function requireSocialListen() {
  if (!socialFeatures.canViewSocialListen()) {
    return { name: "social-reports" };
  }

  return true;
}

export default applyThemeConfig([
  {
    alias: ["/login", "/password-reset"],
    component: () => import("pages/LoginPage.vue"),
    meta: {
      // This var means a not logged user can access the route. Used by the navigation guard
      freeAccess: true,
    },
    name: "login",
    path: "/sign-in",
    props: (route) => ({
      redirect: route.query.redirect,
      resetKey: route.query.reset_key,
      returnTo: normalizeReturnTo(route.query.return_to),
    }),
  },
  {
    component: () => import("src/components/OAuthRedirect.vue"),
    meta: {
      freeAccess: true,
      public: true,
    },
    path: "/oauth/:type",
    props: (route) => ({
      ...route.params,
      ...transformKeys(route.query, "camelCase"),
    }),
  },
  {
    component: () => import("shared/pages/PlanhatPage.vue"),
    name: "planhat",
    path: "/planhat",
  },
  {
    component: () => import("shared/pages/SkilljarPage.vue"),
    name: "skilljar",
    path: "/skilljar",
  },
  {
    component: () => import("shared/pages/OauthLogin.vue"),
    meta: {
      freeAccess: true,
      public: true,
    },
    path: "/oauth",
    props: (route) => ({
      ...route.params,
      ...transformKeys(route.query, "camelCase"),
    }),
  },
  {
    component: () =>
      import("shared/pages/StreamNotificationSubscribe.vue").catch(
        showLoadErrorPage
      ),
    meta: {
      freeAccess: true,
      public: true,
    },
    name: "stream-notification-subscribe",
    path: "/stream-notifications/subscribe",
  },
  {
    component: () =>
      import("shared/pages/StreamNotificationUnsubscribe.vue").catch(
        showLoadErrorPage
      ),
    meta: {
      freeAccess: true,
      public: true,
    },
    name: "stream-notification-unsubscribe",
    path: "/stream-notifications/unsubscribe",
  },
  {
    component: () =>
      import("shared/pages/StreamNotificationSubscribe.vue").catch(
        showLoadErrorPage
      ),
    meta: {
      freeAccess: true,
      splashscreenBackground: "var(--s-color-purple-8)",
    },
    name: "social-stream-notification-subscribe",
    path: "/social/monitor/stream-notifications/subscribe",
  },
  {
    component: () =>
      import("shared/pages/StreamNotificationUnsubscribe.vue").catch(
        showLoadErrorPage
      ),
    meta: {
      freeAccess: true,
      splashscreenBackground: "var(--s-color-purple-8)",
    },
    name: "social-stream-notification-unsubscribe",
    path: "/social/monitor/stream-notifications/unsubscribe",
  },
  {
    children: [
      {
        children: [
          {
            component: () =>
              import("src/pages/LaunchpadDashboard/LaunchpadDashboardMonitoring.vue"),
            meta: {
              product: "monitoring",
              routeKey: "dashboard",
            },
            name: "dashboard-monitoring",
            path: "monitoring",
          },
          {
            component: () =>
              import("src/pages/LaunchpadDashboard/LaunchpadDashboardSocial.vue"),
            meta: {
              product: "social",
              routeKey: "dashboard",
            },
            name: "dashboard-social",
            path: "social",
          },
          {
            component: () =>
              import("src/pages/LaunchpadDashboard/LaunchpadDashboardOutreach.vue"),
            meta: {
              product: "outreach",
              routeKey: "dashboard",
            },
            name: "dashboard-outreach",
            path: "outreach",
          },
        ],
        component: () => import("src/pages/LaunchpadDashboard.vue"),
        meta: {
          canAccess: () => {
            const userStore = useUserStore();
            const { hasLaunchpadBetaEnabled } = storeToRefs(userStore);

            return hasLaunchpadBetaEnabled.value;
          },
          hideHeader: true,
          hideMenu: true,
          routeKey: "dashboard",
        },
        name: "dashboard",
        path: "/launchpad",
        redirect: { name: "dashboard-monitoring" },
      },
      {
        beforeEnter: () => {
          const userStore = useUserStore();
          const { hasLaunchpadBetaEnabled } = storeToRefs(userStore);

          if (hasLaunchpadBetaEnabled.value) {
            const streamsStore = useStreamsStore();
            const { defaultStream } = streamsStore;

            if (defaultStream?.group?.slug && defaultStream?.slug) {
              return {
                path: `/streams/${defaultStream.group.slug}/${defaultStream.slug}`,
              };
            }

            if (features.has("has_front_pages")) {
              return { name: "front-pages" };
            }

            return { name: "instant-insights" };
          }

          return true;
        },
        component: () =>
          import("src/pages/MainPage.vue").catch(showLoadErrorPage),
        meta: {
          features: {
            hasAll: ["has_media_platform", "has_launchpad"],
          },
          title: () => getLocaleText("routes.launchpad"),
        },
        name: "launchpad",
        path: "/monitoring",
        props: (route) => ({ ...route.params, ...route.query }),
      },
      {
        component: () =>
          import("src/pages/MainPage.vue").catch(showLoadErrorPage),
        name: "logout",
        path: "/logout",
        props: true,
      },
      {
        component: () =>
          import("pages/FrontpagesPage.vue").catch(showLoadErrorPage),
        meta: {
          features: {
            has: "has_front_pages",
          },
          title: () => getLocaleText("routes.front_pages"),
        },
        name: "front-pages",
        path: "publishers/frontpages",
      },
      {
        component: () =>
          import("src/pages/DailyHighlightsPage.vue").catch(showLoadErrorPage),
        meta: {
          features: {
            has: "has_daily_highlight",
          },
          title: () => getLocaleText("routes.daily_highlights"),
        },
        name: "daily-highlights",
        path: "daily-highlights/:id?",
        props: true,
      },
      {
        children: [
          {
            component: () =>
              import("src/pages/MentionStreamReport.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              permissions: {
                has: "can_generate_stream_reports",
              },
              title: () => getLocaleText("routes.mention_stream_report"),
            },
            name: "mention-stream-report",
            path: "mention-stream-report/:selectedStreamId?",
            props: true,
          },
          {
            component: () =>
              import("src/components/OrganisationReportAudienceDetail/OrganisationReportAudienceDetailAggregate.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              permissions: {
                has: "can_view_der_audience",
              },
              title: () =>
                getLocaleText("routes.curated_email_report_audience"),
            },
            name: "curated-email-report-aggregate-audience",
            path: "curated-email-report/audience",
          },
          {
            children: [
              {
                component: () =>
                  import("src/components/OrganisationReportAudienceDetail/OrganisationReportAudienceDetailSingle.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  permissions: {
                    has: "can_view_der_audience",
                  },
                  title: () =>
                    getLocaleText(
                      "routes.curated_email_report_audience_overview"
                    ),
                },
                name: "curated-email-report-audience",
                path: "audience",
                props: true,
              },
              {
                beforeEnter() {
                  useTeamRouterViewKey().setEmptyKey();
                },
                component: () =>
                  import("src/pages/OrganisationReport/OrganisationReport.vue").catch(
                    showLoadErrorPage
                  ),
                name: "curated-email-report",
                path: "",
                props: (route) => ({ ...route.params, ...route.query }),
              },
            ],
            component: () =>
              import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
            meta: {
              permissions: {
                has: "can_manage_organisation_reports",
              },
              title: () => getLocaleText("routes.curated_email_report"),
            },
            path: "curated-email-report/:editedReportId?",
            props: (route) => ({ ...route.params, ...route.query }),
          },
          {
            beforeEnter() {
              useTeamRouterViewKey().setEmptyKey();
            },
            component: () =>
              import("src/pages/ScheduledReport.vue").catch(showLoadErrorPage),
            meta: {
              permissions: {
                has: "can_manage_scheduled_reports",
              },
              title: () => getLocaleText("routes.scheduled_report"),
            },
            name: "scheduled-report",
            path: "scheduled-report-specifications/:specId?",
            props: true,
          },
          {
            component: () =>
              import("src/pages/TaggedMentionsReport.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              permissions: {
                has: "can_manage_tagged_reports",
              },
              title: () => getLocaleText("routes.tagged_folders_report"),
            },
            name: "tagged-mentions-report",
            path: "tagged-mentions-report",
            props: true,
          },
          {
            component: () =>
              import("pages/ReportsPage.vue").catch(showLoadErrorPage),
            name: "reports",
            path: "",
            props: (route) => ({ view: route.query.view }),
          },
        ],
        component: () =>
          import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
        meta: {
          permissions: {
            hasAny: [
              "can_generate_stream_reports",
              "can_manage_organisation_reports",
              "can_manage_tagged_reports",
              "can_manage_scheduled_reports",
            ],
          },
          title: () => getLocaleText("routes.reports"),
        },
        path: "reports",
      },
      {
        name: "instant-insights",
        path: "instant-insights",
        redirect: { name: "insights" },
      },
      {
        component: () =>
          import("src/pages/MediaTranscriptRequests.vue").catch(
            showLoadErrorPage
          ),
        meta: {
          title: () =>
            features.has("has_completed_transcripts_page")
              ? getLocaleText("routes.media_and_transcripts")
              : getLocaleText("routes.media"),
        },
        name: "media-and-transcripts",
        path: "media-and-transcripts",
      },
      {
        beforeEnter: (to) => {
          if (outreachFeatures.canViewOutreachPlatform()) {
            if (to.path.replace(/\/$/, "") === "/outreach") {
              const userStore = useUserStore();
              const { hasLaunchpadBetaEnabled } = storeToRefs(userStore);

              const name =
                features.has("has_outreach") && !hasLaunchpadBetaEnabled.value
                  ? "outreach-launchpad"
                  : "outreach-search";

              return { name };
            }

            return true;
          }

          return { name: "launchpad" };
        },
        children: [
          {
            beforeEnter() {
              const userStore = useUserStore();
              const { hasLaunchpadBetaEnabled } = storeToRefs(userStore);

              if (!hasLaunchpadBetaEnabled.value) {
                return true;
              }

              if (!outreachFeatures.search().isDisabled) {
                return { name: "outreach-search" };
              }

              return { name: "dashboard-outreach" };
            },
            component: () =>
              import("src/pages/Outreach/OutreachLaunchpad.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              hideSearchBar: true,
              title: () => getLocaleText("routes.outreach_launchpad"),
            },
            name: "outreach-launchpad",
            path: "launchpad",
            props: (route) => ({ ...route.params, ...route.query }),
          },
          {
            beforeEnter() {
              if (outreachFeatures.quickSearch().isDisabled) {
                return { name: "outreach-launchpad" };
              }

              return true;
            },
            children: [
              {
                beforeEnter(to) {
                  if (Number(to.params.id) === 0) {
                    return { name: "outreach-search" };
                  }

                  return true;
                },
                component: () =>
                  import("src/pages/Outreach/OutreachOutlet.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  reuseComponent: true,
                  title: (route) =>
                    route.params.name || getLocaleText("global.outlet"),
                },
                name: "outreach-search-outlet",
                path: "outlet/:id/:tab?",
                props: (route) => ({
                  id: route.params.id,
                  tab: route.params.tab || "about",
                }),
              },
              {
                component: () =>
                  import("src/pages/Outreach/OutreachInfluencer.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  reuseComponent: true,
                  title: (route) =>
                    route.params.name || getLocaleText("global.journalist"),
                },
                name: "outreach-search-influencer",
                path: "influencer/:id/:tab?",
                props: (route) => ({
                  id: route.params.id,
                  tab: route.params.tab || "about",
                }),
              },
              {
                beforeEnter() {
                  const userStore = useUserStore();
                  const { hasLaunchpadBetaEnabled } = storeToRefs(userStore);

                  const launchpadRouteName = hasLaunchpadBetaEnabled.value
                    ? "dashboard-outreach"
                    : "outreach-launchpad";

                  if (outreachFeatures.search().isDisabled) {
                    return { name: launchpadRouteName };
                  }

                  return true;
                },
                component: () =>
                  import("src/pages/Outreach/OutreachSearch.vue").catch(
                    showLoadErrorPage
                  ),
                name: "outreach-search",
                path: "",
                props: (route) => ({ ...route.params, ...route.query }),
              },
            ],
            component: () =>
              import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
            meta: {
              hideSearchBar: true,
              title: () => getLocaleText("global.search"),
            },
            path: "search",
          },
          {
            beforeEnter() {
              if (!outreachFeatures.hasDiscover()) {
                return { name: "outreach-launchpad" };
              }

              return true;
            },
            component: () =>
              import("src/pages/OutreachDiscover/OutreachDiscover.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              title: () => getLocaleText("global.discover"),
            },
            name: "outreach-discover",
            path: "discover",
            props: (route) => ({ ...route.params, ...route.query }),
          },
          {
            beforeEnter() {
              if (outreachFeatures.activities().isDisabled) {
                return { name: "outreach-launchpad" };
              }

              return true;
            },
            children: [
              {
                beforeEnter() {
                  if (
                    !outreachFeatures.sendMediaRelease().isDisabled &&
                    outreachFeatures.hasAiPitch()
                  ) {
                    return true;
                  }

                  return { name: "outreach-activities" };
                },
                component: () =>
                  import("src/pages/Outreach/OutreachPitchEdit.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: () => getLocaleText("routes.edit_pitch"),
                },
                name: "pitch-edit",
                path: "pitch/:id",
                props: (route) => ({
                  autoGenerate: route.query.autoGenerate === "true",
                  id: route.params.id,
                }),
              },
              {
                beforeEnter() {
                  if (outreachFeatures.sendMediaRelease().isDisabled) {
                    return { name: "outreach-activities" };
                  }

                  return true;
                },
                component: () =>
                  import("src/pages/Outreach/MediaReleaseEdit.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: () => getLocaleText("routes.create_media_release"),
                },
                name: "media-release-new",
                path: "media-release/new",
                props: true,
              },
              {
                beforeEnter() {
                  if (outreachFeatures.sendMediaRelease().isDisabled) {
                    return { name: "outreach-activities" };
                  }

                  return true;
                },
                component: () =>
                  import("src/pages/Outreach/MediaReleaseEdit.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: () => getLocaleText("routes.edit_media_release"),
                },
                name: "media-release-edit",
                path: "media-release/:id",
                props: (route) => ({ ...route.params, ...route.query }),
              },
              {
                beforeEnter() {
                  if (outreachFeatures.mediaReleaseAnalytics().isDisabled) {
                    return { name: "outreach-activities" };
                  }

                  return true;
                },
                component: () =>
                  import("src/pages/Outreach/OutreachMediaReleaseAnalytics.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: (route) =>
                    route.query.subject
                      ? route.query.subject
                      : getLocaleText("global.analytics"),
                },
                name: "media-release-analytics",
                path: ":id/analytics",
                props: (route) => ({ ...route.params, ...route.query }),
              },
              {
                component: () =>
                  import("src/pages/Outreach/OutreachActivities.vue").catch(
                    showLoadErrorPage
                  ),
                name: "outreach-activities",
                path: "",
                props: (route) => ({ ...route.params, ...route.query }),
              },
            ],
            component: () =>
              import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
            meta: {
              hideSearchBar: true,
              title: () => getLocaleText("routes.outreach_activities"),
            },
            path: "activities",
          },
          {
            beforeEnter() {
              if (outreachFeatures.contactLists().isDisabled) {
                return { name: "outreach-launchpad" };
              }

              return true;
            },
            children: [
              {
                component: () =>
                  import("src/pages/Outreach/OutreachContactListsContacts.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: (route) => {
                    if (route.query.listLabel) {
                      return route.query.listLabel;
                    }

                    return getLocaleText("routes.outreach_contacts");
                  },
                },
                name: "outreach-lists-contacts",
                path: ":listId/contacts",
                props: true,
              },
              {
                component: () =>
                  import("src/pages/Outreach/OutreachContactMailingListsContacts.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: (route) => {
                    if (route.query.listLabel) {
                      return route.query.listLabel;
                    }

                    return getLocaleText("routes.outreach_contacts");
                  },
                },
                name: "outreach-mailing-lists-contacts",
                path: "mailing-lists/:contactMailingListId/contacts",
                props: true,
              },
              {
                component: () =>
                  import("src/pages/Outreach/OutreachLists.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  routeKey: "outreach-lists",
                },
                name: "outreach-lists",
                path: ":tab?",
                props: (route) => ({ ...route.params, ...route.query }),
              },
            ],
            component: () =>
              import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
            meta: {
              hideSearchBar: true,
              title: () => getLocaleText("routes.outreach_lists"),
            },
            path: "lists",
          },
        ],
        component: () => import("src/pages/Outreach").catch(showLoadErrorPage),
        name: "outreach",
        path: "outreach",
      },
      {
        children: [
          {
            children: [
              { path: "", redirect: { name: "social-stream-notifications" } },
              {
                component: () =>
                  import("src/pages/StreamNotifications.vue").catch(
                    showLoadErrorPage
                  ),
                name: "social-stream-notifications",
                path: "",
              },
              {
                component: () =>
                  import("shared/pages/StreamNotificationsEdit.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: (route) =>
                    route.params.notificationId
                      ? getLocaleText("routes.notification_edit")
                      : getLocaleText("routes.notification_create"),
                },
                name: "social-stream-notifications-edit",
                path: "notification/:notificationId?",
                props: (route) => typecastRouteProps(route),
              },
            ],
            component: () =>
              import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
            meta: {
              title: () => getLocaleText("routes.notifications"),
            },
            path: "stream-notifications",
          },
          {
            children: [
              {
                component: () =>
                  import("src/pages/MentionStreamBuilderPage.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: () => getLocaleText("routes.social_stream_create"),
                },
                name: "social-stream-new",
                path: "new",
                props: (route) => ({
                  streamType: streamTypes.socialStream,
                  ...route.params,
                }),
              },
              {
                beforeEnter(to) {
                  if (
                    useTeamRoute(to).isRouteRestricted(
                      useStreamGroupsStore().getStreamGroupBySlug(
                        to.params.groupSlug
                      )?.organisation_team_id
                    )
                  ) {
                    return useProductMenu(true).socialRoute.value;
                  }

                  return true;
                },
                children: [
                  {
                    component: () =>
                      import("src/pages/MentionStreamBuilderPage.vue").catch(
                        showLoadErrorPage
                      ),
                    meta: {
                      title: () => getLocaleText("routes.social_stream_create"),
                    },
                    name: "social-group-stream-new",
                    path: "new",
                    props: (route) => ({
                      streamType: streamTypes.socialStream,
                      ...route.params,
                    }),
                  },
                  {
                    component: () =>
                      import("src/pages/Social/SocialStreamGroupEdit.vue"),
                    meta: {
                      parent: "stream",
                      title: () => getLocaleText("routes.social_group_edit"),
                    },
                    name: "social-stream-group-edit",
                    path: "edit",
                    props: true,
                  },
                  {
                    children: [
                      {
                        component: () => import("src/components/SocialReport"),
                        meta: {
                          title: () =>
                            getLocaleText("routes.social_report_create"),
                        },
                        name: "social-report",
                        path: "report/:selectedStreamId",
                        props: true,
                      },
                      {
                        children: [
                          {
                            children: [
                              {
                                component: () =>
                                  import("src/pages/Social/SocialStreamNotificationEdit.vue").catch(
                                    showLoadErrorPage
                                  ),
                                meta: {
                                  title: (route) =>
                                    route.params.notificationId
                                      ? getLocaleText(
                                          "routes.notification_edit"
                                        )
                                      : getLocaleText(
                                          "routes.notification_create"
                                        ),
                                },
                                name: "social-stream-notification-edit",
                                path: "notification/:notificationId?",
                                props: true,
                              },
                              {
                                component: () =>
                                  import("src/pages/Social/SocialStreamEdit.vue"),
                                meta: {
                                  reuseComponent: true,
                                },
                                name: "social-stream-edit",
                                path: "",
                                props: true,
                              },
                            ],
                            component: () =>
                              import("src/layouts/BaseLayout.vue").catch(
                                showLoadErrorPage
                              ),
                            meta: {
                              title: () =>
                                getLocaleText("routes.social_stream_edit"),
                            },
                            path: ":tab?",
                          },
                        ],
                        component: () =>
                          import("src/layouts/BaseLayout.vue").catch(
                            showLoadErrorPage
                          ),
                        path: "edit",
                        props: true,
                      },
                      {
                        component: () =>
                          import("src/pages/Social/SocialTabMonitor.vue"),
                        meta: {
                          reuseComponent: true,
                        },
                        name: "social-monitor",
                        path: "",
                        props: (route) => ({
                          ...route.params,
                          shared: route.params.groupSlug === "shared",
                        }),
                      },
                    ],
                    component: () => import("src/layouts/BaseLayout.vue"),
                    meta: {
                      title: (route) => {
                        const stream = useStreamsStore().getStreamBySlug(
                          route.params.streamSlug
                        );

                        if (!stream) {
                          return null;
                        }

                        return stream.label;
                      },
                    },
                    path: ":streamSlug?",
                  },
                ],
                component: () => import("src/layouts/BaseLayout.vue"),
                meta: {
                  title: (route) => {
                    if (route.params.groupSlug === "shared") {
                      return getLocaleText("routes.shared_social_streams");
                    }

                    const streamGroup =
                      useStreamGroupsStore().getStreamGroupBySlug(
                        route.params.groupSlug
                      );

                    if (streamGroup) {
                      return streamGroup.label;
                    }

                    return getLocaleText("routes.listen");
                  },
                },
                path: ":groupSlug?",
              },
              {
                component: () =>
                  import("src/pages/Social/SocialTabMonitor.vue"),
                path: "",
                props: true,
              },
            ],
            component: () => import("src/layouts/BaseLayout.vue"),
            meta: {
              canAccess() {
                return socialFeatures.canViewSocialListen();
              },
              title: (route) => {
                if (!route.params.groupSlug)
                  return getLocaleText("routes.listen");

                return null;
              },
            },
            path: "monitor",
          },
          {
            children: [
              {
                children: [
                  {
                    component: () =>
                      import("shared/modules/SocialEngageCompose/components/SocialEngageCompose/SocialEngageCompose.vue"),
                    meta: {
                      title: () => getLocaleText("routes.social_compose_post"),
                    },
                    name: "social-engage-compose",
                    path: "compose/:id?",
                    props: (route) => typecastRouteProps(route),
                  },
                  {
                    component: () =>
                      import("src/components/SocialEngageScheduler"),
                    name: "social-engage",
                    path: "",
                    props: (route) => typecastRouteProps(route),
                  },
                ],
                component: () => import("src/layouts/BaseLayout.vue"),
                meta: {
                  title: () => getLocaleText("routes.social_calendar"),
                },
                path: "",
              },
              {
                component: () => import("src/components/SocialEngageSettings"),
                meta: {
                  canAccess() {
                    return socialFeatures.canManageSocialEngage();
                  },
                  title: () =>
                    getLocaleText("routes.social_account_management"),
                },
                name: "social-engage-settings",
                path: "settings",
                props: (route) => ({
                  action: route.query.action,
                }),
              },
              {
                component: () =>
                  import("shared/modules/SocialEngagePostPerformance/components/SocialEngagePostPerformancePage/SocialEngagePostPerformancePage.vue"),
                meta: {
                  title: () => getLocaleText("routes.social_performance"),
                },
                name: "social-post-performance",
                path: "post-performance",
              },
            ],
            component: () => import("src/pages/Social/SocialTabEngage.vue"),
            meta: {
              canAccess() {
                return socialFeatures.canViewSocialEngage();
              },
            },
            path: "engage",
          },
          {
            children: [
              {
                component: () =>
                  import("src/pages/AccountFormPage.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: () => getLocaleText("global.profile"),
                },
                path: "profile",
              },
              {
                component: () =>
                  import("shared/components/user/UserPreferencesForm.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: () => getLocaleText("routes.preferences"),
                },
                path: "preference",
              },
              {
                component: () =>
                  import("src/pages/SecurityFormPage.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: () => getLocaleText("routes.security"),
                },
                path: "security",
              },
            ],
            component: () => import("pages/AccountPage.vue"),
            meta: {
              title: () => getLocaleText("routes.account"),
            },
            name: "social-account",
            path: ":product/account",
            redirect: (to) => `${to.path.replace(/\/$/, "")}/profile`,
          },
          {
            component: () => import("src/pages/Social/SocialTabAnalyse.vue"),
            meta: {
              canAccess() {
                return socialFeatures.canViewSocialAnalyse();
              },
              title: () => getLocaleText("routes.social_analyse"),
            },
            name: "social-analyse",
            path: "analyse",
            redirect: { name: "insights" },
          },
          {
            children: [
              {
                beforeEnter: requireSocialListen,
                component: () =>
                  import("src/components/SocialReport").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: () => getLocaleText("routes.social_report_create"),
                },
                name: "social-report-new",
                path: "new/:selectedStreamId?",
                props: true,
              },
              {
                beforeEnter: requireSocialListen,
                component: () =>
                  import("src/pages/ScheduledReport.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  product: "social",
                  title: () =>
                    getLocaleText("routes.social_scheduled_report_new"),
                },
                name: "social-scheduled-report-new",
                path: "scheduled-report/new",
              },
              {
                beforeEnter: requireSocialListen,
                component: () =>
                  import("src/pages/ScheduledReport.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  product: "social",
                  title: () => getLocaleText("routes.social_scheduled_report"),
                },
                name: "social-scheduled-report",
                path: "scheduled-report-specifications/:specId",
                props: true,
              },
              {
                component: () =>
                  import("src/pages/Social/SocialReports.vue").catch(
                    showLoadErrorPage
                  ),
                name: "social-reports",
                path: ":preSelectedTabLabel?",
                props: (route) => {
                  const viewQuery = route.query.view;

                  const normalizedView = Array.isArray(viewQuery)
                    ? viewQuery[0]
                    : (viewQuery ?? undefined);

                  return {
                    preSelectedTabLabel:
                      route.params.preSelectedTabLabel || undefined,
                    view: normalizedView,
                  };
                },
              },
            ],
            component: () =>
              import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
            meta: {
              canAccess() {
                return (
                  socialFeatures.canViewSocialAnalyse() ||
                  socialFeatures.canViewSocialListen() ||
                  socialFeatures.canViewSocialEngage()
                );
              },
              title: () => getLocaleText("routes.social_reports"),
            },
            path: "reports",
          },
          {
            children: [
              {
                children: [
                  {
                    beforeEnter(to) {
                      if (
                        useTeamRoute(to).isRouteRestricted(
                          useStreamsStore().getStreamBySlug(
                            to.params.streamSlug
                          )?.organisation_team_id
                        )
                      ) {
                        return useProductMenu(true).socialRoute.value;
                      }

                      return true;
                    },
                    children: [
                      {
                        component: () => import("src/components/SocialReport"),
                        meta: {
                          title: () =>
                            getLocaleText("routes.bookmarks_report_create"),
                        },
                        name: "social-bookmark-report",
                        path: "report/:selectedStreamId",
                        props: true,
                      },
                      {
                        component: () =>
                          import("src/layouts/StreamsLayout.vue").catch(
                            showLoadErrorPage
                          ),
                        meta: {
                          reuseComponent: true,
                        },
                        name: "socialBookmarkStream",
                        path: "",
                        props: (route) => ({
                          bookmarks: true,
                          social: true,
                          ...route.params,
                          shared: route.params.groupSlug === "shared",
                        }),
                      },
                      {
                        children: [
                          {
                            children: [
                              {
                                component: () =>
                                  import("src/pages/BookmarkStreamEdit.vue").catch(
                                    showLoadErrorPage
                                  ),
                                meta: {
                                  reuseComponent: true,
                                },
                                name: "social-bookmark-stream-edit",
                                path: "",
                                props: (route) => ({
                                  bookmarks: true,
                                  social: true,
                                  ...route.params,
                                  isSocialStream: true,
                                }),
                              },
                            ],
                            component: () =>
                              import("src/layouts/BaseLayout.vue").catch(
                                showLoadErrorPage
                              ),
                            meta: {
                              title: () =>
                                getLocaleText("routes.tagged_folder_edit"),
                            },
                            path: ":tab?",
                            props: true,
                          },
                        ],
                        component: () =>
                          import("src/layouts/BaseLayout.vue").catch(
                            showLoadErrorPage
                          ),
                        path: "edit",
                        props: true,
                      },
                    ],
                    component: () =>
                      import("src/layouts/BaseLayout.vue").catch(
                        showLoadErrorPage
                      ),
                    meta: {
                      title: (route) => {
                        const stream = useStreamsStore().getStreamBySlug(
                          route.params.streamSlug
                        );

                        if (!stream) {
                          return null;
                        }

                        return stream.label;
                      },
                    },
                    path: ":streamSlug?",
                    props: true,
                  },
                ],
                component: () =>
                  import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
                meta: {
                  route: (path, route) => {
                    if (route.params.groupSlug === "launchpad") {
                      return "/";
                    }

                    return path;
                  },
                  title: (route) => {
                    if (route.params.groupSlug === "shared")
                      return getLocaleText("routes.shared_streams");
                    if (route.params.groupSlug === "launchpad")
                      return getLocaleText("routes.launchpad");

                    const streamGroup =
                      useStreamGroupsStore().getStreamGroupBySlug(
                        route.params.groupSlug
                      );

                    return streamGroup?.label;
                  },
                },
                path: ":groupSlug?",
              },
            ],
            component: () =>
              import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
            meta: {
              route: (path, route) => {
                if (route.params.groupSlug) {
                  if (route.params.groupSlug === "shared") {
                    return `${path}/my-tagged`;
                  }

                  return `${path}/${route.params.groupSlug}`;
                }

                return path;
              },
              title: () => getLocaleText("routes.tagged_folders"),
            },
            path: "tagged",
            props: true,
          },
        ],
        component: () => import("src/pages/Social").catch(showLoadErrorPage),
        meta: {
          canAccess() {
            return socialFeatures.canViewSocialPlatform();
          },
          product: "social",
        },
        name: "social",
        path: "social",
      },
      {
        children: [
          {
            component: () =>
              import("src/pages/Crm/CrmLogin.vue").catch(showLoadErrorPage),
            meta: {
              features: {
                has: "has_journalist_enquiries",
              },
              hideSearchBar: true,
              title: () => getLocaleText("routes.login"),
            },
            name: "crm-login",
            path: "/crm/login",
          },
          {
            component: () =>
              import("src/pages/Crm/CrmContent.vue").catch(showLoadErrorPage),
            meta: {
              contentTitle: "Activities",
              features: {
                has: "has_journalist_enquiries",
              },
              icon: "img:https://assets.streem.com.au/icons/enquiry-events.svg",
              title: () => getLocaleText("routes.crm_activities"),
            },
            name: "crm-activities",
            path: "/crm/activities",
          },
          {
            component: () =>
              import("src/pages/Crm/CrmContent.vue").catch(showLoadErrorPage),
            meta: {
              contentTitle: "Open Enquiries",
              features: {
                has: "has_journalist_enquiries",
              },
              icon: "img:https://assets.streem.com.au/icons/open-enquiries.svg",
              title: () => getLocaleText("routes.crm_open_enquiries"),
            },
            name: "crm-open-enquiries",
            path: "/crm/open-enquiries",
          },
          {
            component: () =>
              import("src/pages/Crm/CrmContent.vue").catch(showLoadErrorPage),
            meta: {
              contentTitle: "Closed Enquiries",
              features: {
                has: "has_journalist_enquiries",
              },
              icon: "img:https://assets.streem.com.au/icons/closed-enquiries.svg",
              title: () => getLocaleText("routes.crm_closed_enquiries"),
            },
            name: "crm-closed-enquiries",
            path: "/crm/closed-enquiries",
          },
        ],
        component: () =>
          import("pages/Crm/CrmPage.vue").catch(showLoadErrorPage),
        meta: {
          features: {
            has: "has_journalist_enquiries",
          },
        },
        name: "crm",
        path: "crm",
      },
      {
        component: () => import("src/pages/CSL.vue").catch(showLoadErrorPage),
        meta: {
          hideHeader: true,
          hideMenu: true,
          hideSearchBar: true,
          title: () => getLocaleText("routes.csl"),
        },
        name: "csl",
        path: "csl",
      },
      {
        children: [
          {
            children: [
              {
                component: () =>
                  import("shared/modules/Insights/pages/InsightsDashboards/InsightsDashboards.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: () => getLocaleText("routes.insights_dashboards"),
                },
                name: "insights-dashboards",
                path: "",
              },
              buildInsightsDashboardRoutes({
                isCreate: true,
                type: InsightsLandingPageType.Dashboards,
              }),
              buildInsightsDashboardRoutes({
                type: InsightsLandingPageType.Dashboards,
              }),
            ],
            meta: {
              routeKey: "insights",
              title: () => getLocaleText("routes.insights_dashboards"),
            },
            path: "dashboards",
          },
          {
            component: () =>
              import("shared/modules/Insights/pages/InsightsDownloads/InsightsDownloads.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              routeKey: "insights",
              title: () => getLocaleText("routes.insights_downloads"),
            },
            name: "insights-downloads",
            path: "downloads",
          },
          {
            component: () =>
              import("shared/modules/InsightsScheduledReports/pages/InsightsSchedules/InsightsSchedules.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              hideInsightsSearch: true,
              permissions: {
                has: "can_manage_scheduled_reports",
              },
              routeKey: "insights",
              title: () => getLocaleText("routes.insights_schedules"),
            },
            name: "insights-schedules",
            path: "schedules",
          },
          {
            children: [
              {
                component: () =>
                  import("shared/modules/Insights/pages/InsightsStarred/InsightsStarred.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: () => getLocaleText("routes.insights_starred"),
                },
                name: "insights-starred",
                path: "",
              },
              buildInsightsDashboardRoutes({
                type: InsightsLandingPageType.Starred,
              }),
            ],
            meta: {
              routeKey: "insights",
              title: () => getLocaleText("routes.insights_starred"),
            },
            path: "starred",
          },
          {
            children: [
              {
                component: () =>
                  import("shared/modules/Insights/pages/InsightsShared/InsightsShared.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: () => getLocaleText("routes.insights_shared"),
                },
                name: "insights-shared",
                path: "",
              },
              buildInsightsDashboardRoutes({
                type: InsightsLandingPageType.Shared,
              }),
            ],
            meta: {
              routeKey: "insights",
              title: () => getLocaleText("routes.insights_shared"),
            },
            path: "shared",
          },
          {
            component: () =>
              import("shared/modules/Insights/pages/InsightsArchived/InsightsArchived.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              routeKey: "insights",
              title: () => getLocaleText("routes.insights_archived"),
            },
            name: "insights-archived",
            path: "archived",
          },
        ],
        component: () =>
          import("pages/InsightsPage.vue").catch(showLoadErrorPage),
        meta: {
          hideSearchBar: true,
          newDesign: true,
          routeKey: "insights",
          title: () => getLocaleText("routes.instant_insights"),
        },
        name: "insights",
        path: "insights",
        redirect: { path: "/insights/dashboards" },
      },
      {
        beforeEnter: (to) => {
          if (to.path === "/streams") {
            const { defaultStream } = useStreamsStore();

            if (defaultStream?.group?.slug && defaultStream?.slug) {
              return {
                path: `/streams/${defaultStream.group.slug}/${defaultStream.slug}`,
                replace: true,
              };
            }
          }

          return true;
        },
        children: [
          {
            component: () =>
              import("src/pages/MentionStreamBuilderPage.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              title: () => getLocaleText("routes.mention_stream_create"),
            },
            name: "mention-stream-new",
            path: "new",
            props: true,
          },
          {
            beforeEnter(to) {
              let organisationTeamId;

              if (to.params.groupSlug === "launchpad") {
                organisationTeamId = useStreamsStore().getStreamBySlug(
                  to.params.streamSlug
                )?.group?.organisation_team_id;
              } else {
                organisationTeamId =
                  useStreamGroupsStore().getStreamGroupBySlug(
                    to.params.groupSlug
                  )?.organisation_team_id;
              }

              if (useTeamRoute(to).isRouteRestricted(organisationTeamId)) {
                return "/";
              }

              return true;
            },
            children: [
              {
                component: () =>
                  import("src/pages/MentionStreamBuilderPage.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: () => getLocaleText("routes.mention_stream_create"),
                },
                name: "mention-group-stream-new",
                path: "new",
                props: true,
              },
              {
                component: () =>
                  import("src/pages/MentionStream/MentionStreamGroupEdit.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  parent: "stream",
                  title: () => getLocaleText("routes.stream_group_edit"),
                },
                name: "mention-stream-group-edit",
                path: "edit",
                props: true,
              },
              {
                children: [
                  {
                    component: () =>
                      import("src/pages/MentionStreamReport.vue").catch(
                        showLoadErrorPage
                      ),
                    meta: {
                      title: () =>
                        getLocaleText("routes.mention_stream_report_create"),
                    },
                    name: "mention-stream-report-new",
                    path: "report/:selectedStreamId",
                    props: true,
                  },
                  {
                    children: [
                      {
                        component: () =>
                          import("shared/pages/ExternalItemEdit.vue").catch(
                            showLoadErrorPage
                          ),
                        meta: {
                          title: () =>
                            getLocaleText("routes.external_item_create"),
                        },
                        name: "external-item-new",
                        path: "new/:streamId",
                        props: true,
                      },
                      {
                        component: () =>
                          import("shared/pages/ExternalItemEdit.vue").catch(
                            showLoadErrorPage
                          ),
                        meta: {
                          title: () =>
                            getLocaleText("routes.external_item_edit"),
                        },
                        name: "external-items-edit",
                        path: "edit/:externalItemId",
                        props: true,
                      },
                    ],
                    component: () =>
                      import("src/layouts/BaseLayout.vue").catch(
                        showLoadErrorPage
                      ),
                    path: "external-item",
                    props: true,
                  },
                  {
                    children: [
                      {
                        children: [
                          {
                            component: () =>
                              import("src/pages/MentionStream/MentionStreamNotificationEdit.vue").catch(
                                showLoadErrorPage
                              ),
                            meta: {
                              title: (route) =>
                                route.params.notificationId
                                  ? getLocaleText("routes.notification_edit")
                                  : getLocaleText("routes.notification_create"),
                            },
                            name: "mention-stream-notification-edit",
                            path: "notification/:notificationId?",
                            props: true,
                          },
                          {
                            component: () =>
                              import("src/pages/MentionStream/MentionStreamEdit.vue").catch(
                                showLoadErrorPage
                              ),
                            meta: {
                              reuseComponent: true,
                            },
                            name: "mention-stream-edit",
                            path: "",
                            props: true,
                          },
                        ],
                        component: () =>
                          import("src/layouts/BaseLayout.vue").catch(
                            showLoadErrorPage
                          ),
                        meta: {
                          title: () =>
                            getLocaleText("routes.mention_stream_edit"),
                        },
                        path: ":tab?",
                        props: true,
                      },
                    ],
                    component: () =>
                      import("src/layouts/BaseLayout.vue").catch(
                        showLoadErrorPage
                      ),
                    path: "edit",
                    props: true,
                  },
                  {
                    component: () =>
                      import("src/layouts/StreamsLayout.vue").catch(
                        showLoadErrorPage
                      ),
                    meta: {
                      reuseComponent: true,
                    },
                    name: "stream",
                    path: "",
                    props: (route) => ({
                      ...route.params,
                      shared: route.params.groupSlug === "shared",
                    }),
                  },
                ],
                component: () =>
                  import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
                meta: {
                  route: (path, route) => {
                    if (route.params.groupSlug === "launchpad") {
                      return "/";
                    }

                    return path;
                  },
                  title: (route) => {
                    const stream = useStreamsStore().getStreamBySlug(
                      route.params.streamSlug
                    );

                    if (!stream) {
                      return null;
                    }

                    return stream.label;
                  },
                },
                path: ":streamSlug?",
              },
            ],
            component: () =>
              import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
            meta: {
              route: (path, route) => {
                if (route.params.groupSlug === "launchpad") {
                  return "/";
                }

                return path;
              },
              title: (route) => {
                if (route.params.groupSlug === "shared")
                  return getLocaleText("routes.shared_streams");
                if (route.params.groupSlug === "launchpad")
                  return getLocaleText("routes.launchpad");

                const streamGroup = useStreamGroupsStore().getStreamGroupBySlug(
                  route.params.groupSlug
                );

                return streamGroup?.label;
              },
            },
            path: ":groupSlug?",
          },
        ],
        component: () =>
          import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
        path: "streams",
        props: true,
      },
      {
        children: [
          {
            children: [
              {
                beforeEnter(to) {
                  if (
                    useTeamRoute(to).isRouteRestricted(
                      useStreamsStore().getStreamBySlug(to.params.streamSlug)
                        ?.organisation_team_id
                    )
                  ) {
                    return "/";
                  }

                  return true;
                },
                children: [
                  {
                    component: () =>
                      import("shared/pages/ExternalItemEdit.vue").catch(
                        showLoadErrorPage
                      ),
                    meta: {
                      title: () => getLocaleText("routes.external_item_create"),
                    },
                    name: "bookmark-external-item-new",
                    path: "external-item/:streamId",
                    props: true,
                  },
                  {
                    component: () =>
                      import("src/pages/MentionStreamReport.vue"),
                    meta: {
                      title: () =>
                        getLocaleText("routes.mention_stream_report_create"),
                    },
                    name: "bookmark-report",
                    path: "report/:selectedStreamId",
                    props: true,
                  },
                  {
                    component: () =>
                      import("src/layouts/StreamsLayout.vue").catch(
                        showLoadErrorPage
                      ),
                    meta: {
                      reuseComponent: true,
                    },
                    name: "bookmarkStream",
                    path: "",
                    props: (route) => ({
                      bookmarks: true,
                      ...route.params,
                      shared: route.params.groupSlug === "shared",
                    }),
                  },
                  {
                    children: [
                      {
                        children: [
                          {
                            component: () =>
                              import("src/pages/BookmarkStreamEdit.vue").catch(
                                showLoadErrorPage
                              ),
                            meta: {
                              reuseComponent: true,
                            },
                            name: "bookmark-stream-edit",
                            path: "",
                            props: true,
                          },
                        ],
                        component: () =>
                          import("src/layouts/BaseLayout.vue").catch(
                            showLoadErrorPage
                          ),
                        meta: {
                          title: () =>
                            getLocaleText("routes.tagged_folder_edit"),
                        },
                        path: ":tab?",
                        props: true,
                      },
                    ],
                    component: () =>
                      import("src/layouts/BaseLayout.vue").catch(
                        showLoadErrorPage
                      ),
                    path: "edit",
                    props: true,
                  },
                ],
                component: () =>
                  import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
                meta: {
                  route: (path, route) => {
                    if (route.params.groupSlug === "launchpad") {
                      return "/";
                    }

                    return path;
                  },
                  title: (route) => {
                    const stream = useStreamsStore().getStreamBySlug(
                      route.params.streamSlug
                    );

                    if (!stream) {
                      return null;
                    }

                    return stream.label;
                  },
                },
                path: ":streamSlug?",
                props: true,
              },
            ],
            component: () =>
              import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
            meta: {
              route: (path, route) => {
                if (route.params.groupSlug === "launchpad") {
                  return "/";
                }

                return path;
              },
              title: (route) => {
                if (route.params.groupSlug === "shared")
                  return getLocaleText("routes.shared");
                if (route.params.groupSlug === "launchpad")
                  return getLocaleText("routes.launchpad");

                const streamGroup = useStreamGroupsStore().getStreamGroupBySlug(
                  route.params.groupSlug
                );

                return streamGroup?.label;
              },
            },
            path: ":groupSlug?",
          },
        ],
        component: () =>
          import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
        meta: {
          route: (path, route) => {
            if (route.params.launchpad) {
              return "/";
            }

            if (route.params.groupSlug) {
              if (route.params.groupSlug === "shared") {
                return `${path}/my-tagged`;
              }

              return `${path}/${route.params.groupSlug}`;
            }

            return path;
          },
          title: (route) => {
            if (route.params.launchpad) {
              return getLocaleText("routes.launchpad");
            }

            return getLocaleText("routes.tagged_folders");
          },
        },
        path: "tagged",
      },
      {
        children: [
          { path: "", redirect: { name: "stream-notifications" } },
          {
            component: () =>
              import("src/pages/StreamNotifications.vue").catch(
                showLoadErrorPage
              ),
            name: "stream-notifications",
            path: "/stream-notifications",
          },
          {
            component: () =>
              import("shared/pages/StreamNotificationsEdit.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              title: (route) =>
                route.params.notificationId
                  ? getLocaleText("routes.notification_edit")
                  : getLocaleText("routes.notification_create"),
            },
            name: "stream-notifications-edit",
            path: "notification/:notificationId?",
            props: (route) => typecastRouteProps(route),
          },
        ],
        component: () =>
          import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
        meta: {
          title: () => getLocaleText("routes.notifications"),
        },
        path: "stream-notifications",
      },
      {
        component: () =>
          import("src/pages/MainPage.vue").catch(showLoadErrorPage),
        name: "mentionDetailView",
        path: "/view/:prefixMentionId",
        props: (route) => ({
          ...route.params,
          ...route.query,
          keywords: [].concat(
            route.query.keywords || route.query["keywords[]"] || []
          ),
        }),
      },
      {
        children: [
          {
            component: () =>
              import("src/pages/AccountFormPage.vue").catch(showLoadErrorPage),
            meta: {
              routeKey: "account",
              title: () => getLocaleText("global.profile"),
            },
            name: "profile",
            path: "profile",
          },
          {
            component: () =>
              import("shared/components/user/UserPreferencesForm.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              routeKey: "account",
              title: () => getLocaleText("routes.preferences"),
            },
            name: "preference",
            path: "preference",
          },
          {
            component: () =>
              import("src/pages/SecurityFormPage.vue").catch(showLoadErrorPage),
            meta: {
              routeKey: "account",
              title: () => getLocaleText("routes.security"),
            },
            name: "security",
            path: "security",
          },
        ],
        component: () => import("pages/AccountPage.vue"),
        meta: {
          routeKey: "account",
          theme: "product",
          title: () => getLocaleText("routes.account"),
        },
        name: "account",
        path: ":product/account",
        redirect: { name: "profile" },
      },
      {
        children: [
          {
            component: () =>
              import("src/components/FilterSets/FilterSets.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              title: () => getLocaleText("routes.filter_sets"),
            },
            name: "filter-sets",
            path: "filter-sets",
          },
          {
            beforeEnter(to) {
              // redirect to org general route if `product` has invalid value
              if (!QUERY_SNIPPETS_VALID_PRODUCTS.includes(to.params.product)) {
                return {
                  name: "organisation",
                  params: { product: to.params.product },
                };
              }

              return true;
            },
            children: [
              {
                component: () =>
                  import("shared/modules/QuerySnippets/components/QuerySnippetsList/QuerySnippetsList.vue").catch(
                    showLoadErrorPage
                  ),
                name: "query-snippets",
                path: "",
                props: (route) => ({
                  product:
                    route.params.product === "media"
                      ? AppSection.MONITORING
                      : route.params.product,
                }),
              },
              {
                component: () =>
                  import("src/pages/QuerySnippetEditPage.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  hideOrganisationLayout: true,
                  title: () => getLocaleText("routes.query_snippet_create"),
                },
                name: "query-snippet-create",
                path: "new",
                props: (route) => ({ product: route.params.product }),
              },
              {
                beforeEnter: (to) => {
                  const parsedQuerySnippetId = Number(to.params.querySnippetId);

                  if (Number.isNaN(parsedQuerySnippetId)) {
                    return { name: "not-found" };
                  }

                  return true;
                },
                component: () =>
                  import("src/pages/QuerySnippetEditPage.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  hideOrganisationLayout: true,
                  title: () => getLocaleText("routes.query_snippet_edit"),
                },
                name: "query-snippet-edit",
                path: ":querySnippetId/edit",
                props: (route) => ({
                  product: route.params.product,
                  querySnippetId: Number(route.params.querySnippetId),
                }),
              },
              {
                beforeEnter: (to) => {
                  const parsedQuerySnippetId = Number(to.params.querySnippetId);

                  if (Number.isNaN(parsedQuerySnippetId)) {
                    return { name: "not-found" };
                  }

                  return true;
                },
                component: () =>
                  import("src/pages/QuerySnippetEditPage.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  hideOrganisationLayout: true,
                  title: () => getLocaleText("routes.query_snippet_view"),
                },
                name: "query-snippet-view",
                path: ":querySnippetId/view",
                props: (route) => ({
                  product: route.params.product,
                  querySnippetId: Number(route.params.querySnippetId),
                  readOnly: true,
                }),
              },
            ],
            meta: {
              title: () => getLocaleText("routes.query_snippets"),
            },
            path: "query-snippets",
          },
          {
            component: () =>
              import("shared/modules/OrganisationUsage/pages/OrganisationUsagePage/OrganisationUsagePage.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              title: () => getLocaleText("routes.usage"),
            },
            name: "usage",
            path: "usage",
          },
          {
            component: () =>
              import("shared/components/user/RateCardOverview.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              title: () => getLocaleText("routes.rate_card"),
            },
            name: "ratecard",
            path: "ratecard",
          },
          {
            children: [
              {
                beforeEnter(to) {
                  if (!organisationFeatures.canManageBrandProfiles()) {
                    return { name: outreachFeatures.defaultRouteName(to) };
                  }

                  return true;
                },
                component: () =>
                  import("src/pages/BrandProfileEdit.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: () => getLocaleText("routes.brand_profile_create"),
                },
                name: "brand-profiles-new",
                path: "new",
                props: true,
              },
              {
                beforeEnter(to) {
                  if (!organisationFeatures.canManageBrandProfiles()) {
                    return { name: outreachFeatures.defaultRouteName(to) };
                  }

                  return true;
                },
                component: () =>
                  import("src/pages/BrandProfileEdit.vue").catch(
                    showLoadErrorPage
                  ),
                meta: {
                  title: () => getLocaleText("routes.brand_profile_edit"),
                },
                name: "brand-profiles-edit",
                path: ":organisationBrandId/edit",
                props: true,
              },
              {
                component: () =>
                  import("shared/components/user/BrandProfiles.vue").catch(
                    showLoadErrorPage
                  ),
                name: "brand-profiles",
                path: "",
                props: true,
              },
            ],
            component: () =>
              import("src/layouts/BaseLayout.vue").catch(showLoadErrorPage),
            meta: {
              title: () => getLocaleText("routes.brand_profiles"),
            },
            path: "brand-profiles/:type",
            props: true,
          },
          {
            component: () =>
              import("shared/components/user/UserManagement.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              title: () => getLocaleText("routes.users"),
            },
            name: "users",
            path: "users",
          },
          {
            component: () =>
              import("shared/components/user/TeamsManagement.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              title: () => getLocaleText("routes.teams"),
            },
            name: "teams",
            path: "teams",
          },
          {
            component: () =>
              import("shared/components/user/MentionStreamsManagement.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              title: () => getLocaleText("routes.mention_streams"),
            },
            name: "mention-streams",
            path: "mention-streams",
          },
          {
            component: () =>
              import("shared/components/user/MentionStreamsManagement.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              title: () => getLocaleText("routes.social_streams"),
            },
            name: "social-streams",
            path: "social-streams",
          },
          {
            component: () =>
              import("shared/components/user/ClientManagement.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              canAccess() {
                return useUserStore().organisation.pr_agency;
              },
              title: () => getLocaleText("routes.clients"),
            },
            name: "clients",
            path: "clients",
          },
          {
            component: () =>
              import("shared/components/user/TeamOverview.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              title: () => getLocaleText("routes.team"),
            },
            name: "team",
            path: "team",
          },
          {
            component: () =>
              import("shared/components/user/DistributionLists.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              title: () => getLocaleText("routes.distribution_lists"),
            },
            name: "distribution-list",
            path: "distribution-list",
          },
          {
            component: () =>
              import("shared/components/user/UserIntegrations.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              title: () => getLocaleText("routes.integrations"),
            },
            name: "integrations",
            path: "integrations",
          },
          {
            component: () =>
              import("shared/components/user/MetricEditor.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              title: () => getLocaleText("routes.metric_editor"),
            },
            name: "metric-editor",
            path: "metric-editor",
          },
          {
            component: () =>
              import("shared/components/user/DomainManagement.vue").catch(
                showLoadErrorPage
              ),
            meta: {
              features: {
                has: "has_domain_management",
              },
              title: () => getLocaleText("routes.domains"),
            },
            name: "domains",
            path: "domains",
          },
        ],
        component: () => import("pages/OrganisationPage.vue"),
        meta: {
          routeKey: "organisation",
          theme: "product",
          title: () => getLocaleText("routes.organisation"),
        },
        name: "organisation",
        path: ":product/organisation",
      },
    ],
    component: () => import("src/layouts/DefaultLayout.vue"),
    path: "/",
    redirect: { name: "dashboard" },
  },
  {
    component: () =>
      import("src/pages/OrganisationReportSpecificationsPopup.vue").catch(
        showLoadErrorPage
      ),
    meta: {
      permissions: {
        has: "can_manage_organisation_reports",
      },
    },
    name: "organisation-report-specifications",
    path: "/organisation-report-specifications/:specId",
    props: true,
  },
  {
    component: () =>
      import("src/pages/OrganisationReportPreviewPage.vue").catch(
        showLoadErrorPage
      ),
    meta: {
      permissions: {
        has: "can_manage_organisation_reports",
      },
    },
    name: "preview-curated-email-report",
    path: "/preview-report/:reportId?",
    props: true,
  },
  {
    component: () =>
      import("src/pages/MobileTrap.vue").catch(showLoadErrorPage),
    meta: {
      public: true,
    },
    name: "mobile-trap",
    path: "/mobile",
    props: true,
  },
  {
    // Always leave this as last one
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
]);
